import { bareName, describeTool } from './toolDescriptors'

/**
 * Turning an assistant proposal into a ticket.
 *
 * The agent proposes with the `propose_ticket` MCP tool, which writes nothing:
 * its call arrives here as a typed segment and becomes a button under the
 * answer. Clicking hands the report widget what it cannot produce itself — a
 * description already written by the agent, and the conversation as a markdown
 * file — and the EXISTING report flow does the rest: capture, annotation,
 * project resolution, the same form, the editor's own session and rights.
 *
 * The description is written when the proposal is made, not on click: it is
 * already paid for by the turn the editor has just read, so the button opens
 * instantly and still works once the conversation is over.
 *
 * The handover goes through the widget (`window.OrbitReport`) rather than
 * straight to the in-page event, because the widget is the one that knows WHICH
 * Orbit instance receives the tickets (orbit_report.settings.target_origin).
 * Dispatching the event ourselves would file every ticket on the instance we
 * happen to be looking at.
 */

export const PROPOSE_TICKET_TOOL = 'propose_ticket'

/**
 * The proposal carried by an assistant turn, or null.
 *
 * Only the LAST one counts: the prompt asks for a single proposal per answer,
 * and if the model ever ignores that, two buttons for one request would be
 * worse than one.
 */
export function proposalOf(msg) {
  if (!msg || msg.role !== 'assistant') return null
  const segments = Array.isArray(msg.segments) ? msg.segments : []
  for (let i = segments.length - 1; i >= 0; i--) {
    const seg = segments[i]
    if (seg?.type !== 'tool_use' || bareName(seg.name) !== PROPOSE_TICKET_TOOL) continue
    const input = seg.input && typeof seg.input === 'object' ? seg.input : {}
    // Arguments only land with the final `assistant` message — mid-stream the
    // input is still `{}`. Showing the button then would open an empty form.
    if (!input.title || !input.description) return null
    return {
      id: seg.id ?? null,
      title: String(input.title),
      description: String(input.description),
      reason: input.reason ? String(input.reason) : '',
    }
  }
  return null
}

const stamp = () => new Date().toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' })

/**
 * The conversation as markdown — what a screenshot of the panel cannot give: the
 * text of every turn, searchable, and the tool calls that show what the agent
 * actually looked at.
 *
 * Markdown rather than HTML because the ticket renders it with the same
 * component and the same dialect as this chat, so it reads exactly as it was
 * written — and because a markdown file cannot execute anything wherever it
 * lands.
 *
 * Reasoning segments are left out: they are the agent's scratch work, shown
 * folded here, and they would bury the exchange a developer needs to read.
 */
export function transcriptMarkdown(messages, { title, description } = {}) {
  const parts = ["# Conversation avec l'assistant du builder", '', `_Historique joint au signalement, exporté le ${stamp()}._`]
  // Le ticket proposé est repris ici en entier. La description voyage aussi
  // jusqu'au formulaire, mais par l'URL quand la fenêtre s'ouvre sur une AUTRE
  // instance — donc tronquée si elle est longue. Ce fichier-ci en garde le texte
  // complet, et il est joint dans tous les cas.
  if (title || description) {
    parts.push('', '## Ticket proposé', '')
    if (title) parts.push(`**${title}**`)
    if (description) parts.push('', description)
  }

  for (const msg of Array.isArray(messages) ? messages : []) {
    if (msg?.role === 'user') {
      const text = (msg.text ?? '').trim()
      const names = (msg.attachments ?? []).map((a) => a?.name).filter(Boolean)
      if (!text && names.length === 0) continue
      parts.push('', '## Éditeur', '')
      if (text) parts.push(text)
      // The files themselves are context for one turn and are never stored, so
      // naming them is all we can honestly do.
      if (names.length) parts.push('', `_Pièces jointes au message : ${names.join(', ')}._`)
      continue
    }
    if (msg?.role !== 'assistant') continue

    const body = []
    for (const seg of Array.isArray(msg.segments) ? msg.segments : []) {
      if (seg?.type === 'text' && String(seg.text ?? '').trim()) body.push(String(seg.text).trim())
      else if (seg?.type === 'tool_use') body.push(`> 🔧 ${describeTool(seg.name, seg.input).text}`)
    }
    if (body.length === 0) continue
    parts.push('', '## Assistant', '', body.join('\n\n'))
  }

  return `${parts.join('\n')}\n`
}

/** Timestamped: two transcripts filed the same day must not collide. */
const transcriptName = () => `conversation-${new Date().toISOString().replace(/[:.]/g, '-')}.md`

/**
 * Hand the proposal to the report widget.
 *
 * Returns false when the widget is absent — which should not happen, it is
 * loaded on every Orbit page — rather than falling back to the in-page event:
 * that fallback would silently file the ticket on the instance being looked at,
 * which on a development instance is exactly where nobody reads them.
 */
export function openTicketReport({ proposal, messages }) {
  const widget = typeof window !== 'undefined' ? window.OrbitReport : null
  if (!widget || typeof widget.open !== 'function') return false

  widget.open({
    title: proposal?.title ?? '',
    description: proposal?.description ?? '',
    transcript: {
      name: transcriptName(),
      blob: new Blob(
        [transcriptMarkdown(messages, { title: proposal?.title, description: proposal?.description })],
        { type: 'text/markdown' },
      ),
    },
  })
  return true
}
